import { Suspense, lazy, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import NavigationDefault from 'app/components/Navigation/NavigationDefault';
import { NavigationDetail } from 'app/components/Navigation/core/types';
import TableComponent from 'app/components/Table/TableComponent';
import { QUERIES } from 'app/components/Table/core/constants';
import { stringifyRequestQuery } from 'app/components/Table/core/helpers';
import { TYPE_MODAL } from 'app/components/Table/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { useAuth } from 'app/modules/auth';
import { LOGIN_TYPE } from 'app/modules/auth/core/enum';
import { TAB_TABLET } from 'app/modules/system-settings/users/core/type';

import { usePolicyInUser } from 'app/hooks/policy';

import { PATH } from 'app/constants/path';

import { getSecurityGroups } from '../security-groups/core/requests';
import { SecurityGroup } from '../security-groups/core/type';
import { usersColumns } from './core/columns';
import { getUserById, getUsers } from './core/requests';
import FormChangeStatusCard from './modal/FormChangeStatusCard';
import { FormResetPasswordCard } from './modal/FormResetPasswordCard';
import { FormUserTableCard } from './modal/FormUserTableCard';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UserTabletActivityLogPage = lazy(
  () => import('app/modules/system-settings/users/UserTabletActivityLogPage'),
);

const Main = styled.div`
  .custom-input-search {
    width: 400px;
  }
`;

const UserTabletManagement = () => {
  const { currentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const params = useParams<{ orgId: string }>();
  const orgId = useOrganizationStore((state) => state.state.organization?.id);
  const organizationId = params.orgId ? Number(params.orgId) : orgId;

  const isPageSystemUsers = location.pathname.includes(`${PATH.USER_MANAGEMENT_SYSTEM}`);
  const isSystemAdminCurrent = permission.systemAdmin;

  const [tab, setTab] = useState<TAB_TABLET>(TAB_TABLET.TABLET);
  const [securityGroups, setSecurityGroups] = useState<SecurityGroup[]>([]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    const dataRes = await getSecurityGroups(
      stringifyRequestQuery({
        page: 1,
        limit: 100,
      }) + `&type=2`,
    );
    setSecurityGroups(dataRes.data?.items ?? []);
  };
  const renderModalForm = (originalData: any, typeModal: TYPE_MODAL, isView?: boolean) => {
    if (typeModal === TYPE_MODAL.RESET_PASSWORD) {
      return <FormResetPasswordCard user={originalData} />;
    }
    return (
      <FormUserTableCard
        user={originalData}
        securityGroups={securityGroups}
        loginType={LOGIN_TYPE.TABLET}
        originIsView={isView}
      />
    );
  };
  const renderFormConfirmation = (originalData: any, _typeModal: TYPE_MODAL) => {
    return (
      <FormChangeStatusCard originalData={originalData} nameQuery={QUERIES.USERS_TABLET_LIST} />
    );
  };

  const navigationLocation: NavigationDetail[] = [
    {
      title: 'Tablet',
      value: TAB_TABLET.TABLET,
    },
    {
      title: 'Activity log',
      value: TAB_TABLET.ACTIVITY_LOG,
    },
  ];

  const handleChangeTab = (newValue: TAB_TABLET) => () => {
    setTab(newValue);
  };

  return (
    <Main>
      <NavigationDefault
        navigation={navigationLocation}
        valueActive={tab}
        onChangeNavigation={handleChangeTab}
      />
      {tab === TAB_TABLET.TABLET ? (
        <TableComponent
          getList={getUsers}
          nameQuery={QUERIES.USERS_TABLET_LIST}
          originColumns={usersColumns(LOGIN_TYPE.TABLET)}
          titleModal="Account"
          getById={getUserById}
          ModalForm={renderModalForm}
          ModalFormConfirmation={renderFormConfirmation}
          nameBtnAdd="Add account"
          moreQuery={
            `&type=${LOGIN_TYPE.TABLET}` +
            (isSystemAdminCurrent && !isPageSystemUsers ? `&orgId=${organizationId}` : '')
          }
          placeholderSearch="Search with username, first name, last name"
          isBorder={false}
          spacing="lg"
        />
      ) : (
        <Suspense fallback={<LoadingCard />}>
          <UserTabletActivityLogPage />
        </Suspense>
      )}
    </Main>
  );
};

export default UserTabletManagement;
